import apiClient from './client';

/**
 * Release Notes — picking, authorization, collection workflow.
 *
 * Backend lives at `/release-notes` (separate router, not nested under
 * /sales). Each sale can have at most ONE release note (backend returns
 * 409 if you try to create a second).
 *
 * State machine: created → picked → authorized → collected
 *   - create:    sales rep marks "ready to release" (mobile)
 *   - pick:      warehouse staff confirms each line item picked off the
 *                shelf (typically desktop, but mobile can do it for small
 *                teams; backend requires ALL items to be picked before
 *                the transition succeeds)
 *   - authorize: manager signs off (typically desktop)
 *   - collect:   rep on-site captures receiver name + signature, backend
 *                generates the final stamped PDF (mobile is the natural
 *                home for this — touchscreen signature)
 *
 * All endpoints return the enriched ReleaseNoteOut (with items[],
 * customer_name, invoice_number, *_by_name fields filled in). Drop the
 * response straight into local state to refresh the page.
 *
 * Auth: every endpoint just requires `get_current_user` — no role gates
 * at the API layer. UX gating happens client-side based on who's
 * physically present at each step.
 */

/**
 * POST /release-notes — create a release note from a sale.
 *
 * Body: { sale_id, notes? }
 *
 * Items are auto-populated from the sale's line items. Returns 409 if a
 * release note already exists for that sale, 400 if the sale has no line
 * items, 404 if the sale doesn't exist.
 */
export async function createReleaseNote(saleId, notes) {
  const body = { sale_id: saleId };
  if (notes != null && notes !== '') body.notes = notes;
  const response = await apiClient.post('/release-notes', body);
  return response.data; // ReleaseNoteOut
}

/**
 * GET /release-notes/{id} — full ReleaseNoteOut.
 */
export async function getReleaseNote(rnId) {
  const response = await apiClient.get(`/release-notes/${rnId}`);
  return response.data;
}

/**
 * GET /release-notes?sale_id={saleId} — list (filtered).
 *
 * Used by SaleDetail to find out whether a sale has an associated
 * release note (and what state it's in) without forcing the user to
 * navigate first. Returns ReleaseNoteListItem[] (lighter than full
 * ReleaseNoteOut — no items[]).
 *
 * Each sale has at most one RN, so callers can do `result[0] ?? null`.
 */
export async function listReleaseNotesForSale(saleId) {
  const response = await apiClient.get('/release-notes', {
    params: { sale_id: saleId },
  });
  return response.data; // ReleaseNoteListItem[]
}

/**
 * POST /release-notes/{id}/pick
 *
 * Body: { picked_items: [release_note_item_id, ...] }
 *
 * Backend requires ALL items to be marked picked in a single call —
 * partial picks return 400. Status must be "created"; transitions to
 * "picked" with picked_by + picked_at set from the current user.
 */
export async function pickReleaseNote(rnId, pickedItemIds) {
  const response = await apiClient.post(
    `/release-notes/${rnId}/pick`,
    { picked_items: pickedItemIds },
  );
  return response.data;
}

/**
 * POST /release-notes/{id}/authorize — no body.
 *
 * Status must be "picked"; transitions to "authorized" with
 * authorized_by + authorized_at set from the current user.
 */
export async function authorizeReleaseNote(rnId) {
  const response = await apiClient.post(`/release-notes/${rnId}/authorize`);
  return response.data;
}

/**
 * POST /release-notes/{id}/collect
 *
 * Body: { receiver_name: string, signature: string (base64 data URI) }
 *
 * Status must be "authorized"; transitions to "collected" with
 * collected_at + receiver_name + receiver_signature stored. Backend
 * generates the final stamped PDF and stores it under
 * /uploads/release_notes/{release_number}.pdf — pdf_url on the response
 * points at it.
 *
 * The signature is captured by the SignaturePad component as a JPEG
 * data URI (~5KB at 300x150 q=0.5) — small enough to send in JSON
 * without multipart.
 */
export async function collectReleaseNote(rnId, { receiverName, signature }) {
  const response = await apiClient.post(`/release-notes/${rnId}/collect`, {
    receiver_name: receiverName,
    signature,
  });
  return response.data;
}

/**
 * GET /release-notes/{id}/pdf
 *
 * For "collected" RNs the backend serves the stored stamped PDF; for
 * earlier states it generates a live preview on demand. Auth-gated, so
 * fetch as a blob via apiClient (which adds the Bearer token), then
 * caller wraps in URL.createObjectURL() to view in a new tab.
 */
export async function getReleaseNotePdfBlob(rnId) {
  const response = await apiClient.get(`/release-notes/${rnId}/pdf`, {
    responseType: 'blob',
  });
  return response.data; // Blob
}
